// Personal Drive (My Files) API layer.
//
// Reads (tree/folder) + create-folder share one code path: fetch(apiUrl(...)) —
// MSW intercepts in maket mode, the real backend serves the same shapes in
// real-engine mode. Upload/star/trash differ by mode (real presigned PUT vs the
// mock's single metadata POST), so those branch on USE_REAL_API.

import { apiUrl, USE_REAL_API } from '../lib/api'
import { uploadOneReal } from './drive'
import type {
  DriveFileKind,
  DriveTree,
  FolderListing,
  PersonalDriveFile,
  PersonalDriveFolder,
} from '../mocks/data/drive-personal/store'

export type {
  DriveTree,
  FolderListing,
  PersonalDriveFile,
  PersonalDriveFolder,
  DriveFileKind,
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { detail?: { code?: string; message?: string } }
      | null
    throw new Error(body?.detail?.message ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

export function fetchDriveTree(): Promise<DriveTree> {
  return fetch(apiUrl('/drive/my/tree')).then((r) => jsonOrThrow<DriveTree>(r))
}

export function fetchFolder(folderId: string): Promise<FolderListing> {
  return fetch(apiUrl(`/drive/my/folders/${folderId}`)).then((r) =>
    jsonOrThrow<FolderListing>(r),
  )
}

export function createDriveFolder(input: {
  parent_id: string | null
  name: string
}): Promise<PersonalDriveFolder> {
  return fetch(apiUrl('/drive/my/folders'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  }).then((r) => jsonOrThrow<PersonalDriveFolder>(r))
}

// Real upload = presigned three-step (upload-url → PUT to storage → confirm),
// reusing the existing /drive/files/* endpoints; the personal folder is a known
// component so they accept it. Maket = the single metadata POST the MSW handler
// simulates. The caller passes the real File either way (maket ignores the bytes).
export async function uploadPersonalFile(
  folderId: string,
  file: File,
): Promise<void> {
  if (USE_REAL_API) {
    await uploadOneReal(folderId, file, 'document')
    return
  }
  // Maket: single metadata POST the MSW handler simulates.
  await fetch(apiUrl('/drive/my/files'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      folder_id: folderId,
      name: file.name,
      size_bytes: file.size,
      kind: kindForFile(file.name, file.type),
    }),
  }).then((r) => jsonOrThrow<PersonalDriveFile>(r))
}

export async function starPersonalFile(
  fileId: string,
  starred: boolean,
): Promise<void> {
  await fetch(apiUrl(`/drive/my/files/${fileId}/star`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ starred }),
  }).then((r) => jsonOrThrow<PersonalDriveFile>(r))
}

// Move a personal file into another personal folder. Same endpoint in maket +
// real mode (the MSW handler simulates it); moving is a metadata-only folder_id
// change, so no presigned dance is needed.
export async function movePersonalFile(
  fileId: string,
  folderId: string,
): Promise<void> {
  await fetch(apiUrl(`/drive/my/files/${fileId}/move`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder_id: folderId }),
  }).then((r) => jsonOrThrow<PersonalDriveFile>(r))
}

export async function trashPersonalFile(fileId: string): Promise<void> {
  const path = USE_REAL_API ? `/drive/files/${fileId}` : `/drive/my/files/${fileId}`
  const res = await fetch(apiUrl(path), { method: 'DELETE' })
  if (!res.ok && res.status !== 204) throw new Error(`delete failed: ${res.status}`)
}

export type FileViewUrl = {
  download_url: string
  filename: string
  file_type: string
  expires_at: string
}

// Mint a presigned URL to view (preview) or download a file. Reuses the shared
// audited /drive/files/{id}/download-url endpoint (works for personal files —
// same ownership model as delete). Only meaningful in real-engine mode.
export async function fetchFileViewUrl(
  fileId: string,
  action: 'preview' | 'download',
): Promise<FileViewUrl> {
  return fetch(
    apiUrl(`/drive/files/${encodeURIComponent(fileId)}/download-url?action=${action}`),
  ).then((r) => jsonOrThrow<FileViewUrl>(r))
}

export const drivePersonalKeys = {
  tree: ['drive-personal', 'tree'] as const,
  folder: (id: string) => ['drive-personal', 'folder', id] as const,
}

function kindForFile(name: string, mime: string): DriveFileKind {
  if (mime.startsWith('image/')) return 'image'
  if (mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf')) return 'pdf'
  return 'doc'
}
